import { useEffect, useRef, useCallback } from 'react'
import type { RefObject } from 'react'
import type { MapRef } from 'react-map-gl'
import type MapboxDraw from '@mapbox/mapbox-gl-draw'
import type { Popup as MapboxPopup, GeoJSONSource } from 'mapbox-gl'
// @ts-expect-error - turf types issue
import * as turf from '@turf/turf'
import {
  convertToGeoJSON,
  ensurePolygonClosed,
} from '../../../utils/mapHelpers'

interface UseEditModeEffectsParams {
  mapRef: RefObject<MapRef | null>
  drawRef: RefObject<MapboxDraw | null>
  popupRef: RefObject<MapboxPopup | null>
  editingZoneId: string | null
  editMode: 'polygon' | 'grid' | null
  getZoneById: (id: string) =>
    | {
        coordinates?: number[][]
        id?: string
        name?: string
        score?: number
      }
    | undefined
  onGridCellPositionChange?: (position: [number, number] | null) => void
  zonesSourceId: string
  zonesLayerId: string
  zonesOutlineLayerId: string
  zonesGeoJSON: GeoJSON.FeatureCollection
  setDragPanEnabled: React.Dispatch<React.SetStateAction<boolean>>
  gridCellPosition?: [number, number] | null
  isDrawReady: boolean
}

const createZoneDrawFeature = (zone: {
  id?: string
  coordinates?: number[][]
  name?: string
  score?: number
}): GeoJSON.Feature => ({
  type: 'Feature',
  id: zone.id,
  geometry: {
    type: 'Polygon',
    coordinates: [ensurePolygonClosed(convertToGeoJSON(zone.coordinates!))],
  },
  properties: {
    zoneId: zone.id,
    name: zone.name,
    score: zone.score ?? 0,
  },
})

export function useEditModeEffects({
  mapRef,
  drawRef,
  popupRef,
  editingZoneId,
  editMode,
  getZoneById,
  onGridCellPositionChange,
  zonesSourceId,
  zonesLayerId,
  zonesOutlineLayerId,
  zonesGeoJSON,
  setDragPanEnabled,
  isDrawReady,
}: UseEditModeEffectsParams) {
  const zoneAddedToDrawRef = useRef<string | null>(null)

  // Handle edit mode changes - show/hide polygon and grid cell accordingly
  const setupGridEdit = useCallback(
    (zone: { coordinates?: number[][] }) => {
      if (!zone.coordinates) return
      const geoJsonCoords = convertToGeoJSON(zone.coordinates)
      const centroid = turf.centroid(turf.polygon([geoJsonCoords]))
      const center: [number, number] = [
        centroid.geometry.coordinates[0],
        centroid.geometry.coordinates[1],
      ]
      onGridCellPositionChange?.(center)

      const draw = drawRef.current
      if (draw) {
        draw.getAll().features.forEach(f => f.id && draw.delete(f.id as string))
      }
    },
    [drawRef, onGridCellPositionChange]
  )

  const setupPolygonEdit = useCallback(
    (zone: {
      coordinates?: number[][]
      id?: string
      name?: string
      score?: number
    }) => {
      if (!zone.coordinates) return
      const draw = drawRef.current
      if (!draw) return

      onGridCellPositionChange?.(null)
      if (zoneAddedToDrawRef.current === editingZoneId) return

      const geoJsonCoords = convertToGeoJSON(zone.coordinates)
      const closedCoords = ensurePolygonClosed(geoJsonCoords)

      const zoneFeature: GeoJSON.Feature = {
        type: 'Feature',
        id: zone.id,
        geometry: { type: 'Polygon', coordinates: [closedCoords] },
        properties: {
          zoneId: zone.id,
          name: zone.name,
          score: zone.score ?? 0,
        },
      }

      draw.add(zoneFeature)
      zoneAddedToDrawRef.current = editingZoneId

      setTimeout(() => {
        if (drawRef.current && editingZoneId === zone.id) {
          try {
            const addedFeature = drawRef.current
              .getAll()
              .features.find(
                f =>
                  (f.properties as { zoneId?: string })?.zoneId === zone.id ||
                  f.id === zone.id
              )
            if (addedFeature?.id) {
              drawRef.current.changeMode('direct_select', {
                featureId: addedFeature.id as string,
              })
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error selecting zone in polygon edit mode:', error)
          }
        }
      }, 50)
    },
    [drawRef, editingZoneId, onGridCellPositionChange]
  )

  const applyEditMode = useCallback(() => {
    if (!editingZoneId || !mapRef.current || !drawRef.current || !isDrawReady)
      return
    const zone = getZoneById(editingZoneId)
    if (!zone?.coordinates?.length) return

    if (editMode === 'grid') setupGridEdit(zone)
    else if (editMode === 'polygon') setupPolygonEdit(zone)
  }, [
    editingZoneId,
    mapRef,
    drawRef,
    getZoneById,
    editMode,
    setupGridEdit,
    setupPolygonEdit,
    isDrawReady,
  ])

  useEffect(() => {
    applyEditMode()
  }, [applyEditMode])

  const hideZonesLayers = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    try {
      const layers = [zonesLayerId, zonesOutlineLayerId]
      layers.forEach(
        lyr =>
          map.getLayer(lyr) && map.setLayoutProperty(lyr, 'visibility', 'none')
      )

      const source = map.getSource(zonesSourceId) as GeoJSONSource
      if (source) source.setData(zonesGeoJSON)

      requestAnimationFrame(() => {
        layers.forEach(
          lyr =>
            map.getLayer(lyr) &&
            map.setLayoutProperty(lyr, 'visibility', 'none')
        )
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error hiding zones layers:', error)
    }
  }, [mapRef, zonesLayerId, zonesOutlineLayerId, zonesSourceId, zonesGeoJSON])

  const cleanupEditMode = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    try {
      const layers = [zonesLayerId, zonesOutlineLayerId]
      layers.forEach(
        lyr =>
          map.getLayer(lyr) &&
          map.setLayoutProperty(lyr, 'visibility', 'visible')
      )

      if (drawRef.current) {
        setDragPanEnabled(true)
        const draw = drawRef.current
        draw.getAll().features.forEach(f => f.id && draw.delete(f.id as string))
        draw.changeMode('simple_select')
        zoneAddedToDrawRef.current = null
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error cleaning up edit mode:', error)
    }
  }, [mapRef, zonesLayerId, zonesOutlineLayerId, drawRef, setDragPanEnabled])

  useEffect(() => {
    if (editingZoneId && popupRef.current) {
      popupRef.current.remove()
      popupRef.current = null
    }
  }, [editingZoneId, popupRef])

  const setupPolygonEditing = useCallback(
    (zoneId: string) => {
      const map = mapRef.current?.getMap()
      const draw = drawRef.current
      if (!map || !draw || !isDrawReady) return

      const zone = getZoneById(zoneId)
      if (!zone?.coordinates?.length) return

      const existing = draw
        .getAll()
        .features.find(
          f =>
            (f.properties as { zoneId?: string })?.zoneId === zoneId ||
            f.id === zoneId
        )

      if (existing || zoneAddedToDrawRef.current === zoneId) {
        zoneAddedToDrawRef.current = zoneId
        return
      }

      hideZonesLayers()
      setDragPanEnabled(true)

      draw.getAll().features.forEach(f => f.id && draw.delete(f.id as string))
      draw.add(createZoneDrawFeature(zone))
      zoneAddedToDrawRef.current = zoneId

      setTimeout(() => {
        if (!drawRef.current || editingZoneId !== zoneId) return
        try {
          const added = drawRef.current
            .getAll()
            .features.find(
              f =>
                (f.properties as { zoneId?: string })?.zoneId === zoneId ||
                f.id === zoneId
            )
          if (added?.id) {
            drawRef.current.changeMode('direct_select', {
              featureId: added.id as string,
            })
            hideZonesLayers()
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error selecting zone in edit mode:', error)
        }
      }, 50)
    },
    [
      mapRef,
      drawRef,
      getZoneById,
      hideZonesLayers,
      setDragPanEnabled,
      editingZoneId,
      isDrawReady,
    ]
  )

  useEffect(() => {
    if (!editingZoneId) {
      cleanupEditMode()
      return
    }

    if (editMode === 'polygon') {
      setupPolygonEditing(editingZoneId)
    }

    if (editingZoneId !== zoneAddedToDrawRef.current) {
      zoneAddedToDrawRef.current = null
    }
  }, [editingZoneId, editMode, cleanupEditMode, setupPolygonEditing])

  // Force update zones source when editingZoneId changes to ensure zone is excluded
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    try {
      const lyrVis = editingZoneId ? 'none' : 'visible'
      if (map.getLayer(zonesLayerId))
        map.setLayoutProperty(zonesLayerId, 'visibility', lyrVis)
      if (map.getLayer(zonesOutlineLayerId))
        map.setLayoutProperty(zonesOutlineLayerId, 'visibility', lyrVis)
    } catch {
      /* Ignore */
    }

    const source = map.getSource(zonesSourceId) as GeoJSONSource
    if (source) source.setData(zonesGeoJSON)
  }, [
    editingZoneId,
    zonesGeoJSON,
    mapRef,
    zonesLayerId,
    zonesOutlineLayerId,
    zonesSourceId,
  ])
}
